import { useState, useEffect } from 'react';
import { graphql } from '@lib/api';
import { gql } from '@apollo/client';
import {
  AGENT_BY_ID_QUERY,
  LISTINGS_BY_AGENT_ID_QUERY,
  NEW_OFFICE_LISTINGS,
  LISTINGS_BY_AGENT_ID_PLUS_TEAM_QUERY
} from '@lib/fragments';
import classNames from 'classnames';
import { useUserInfo } from '@lib/user-context';
import { getSlug, toKebabCase } from '@lib/utils';
import { useRouter } from 'next/router';

import { fixtures } from '@content/results';
import designations from '@data/designations.json';
import officeList from '@data/office-list.json';

import Layout from '@layout/index';
import Image from '@components/image';
import Icon from '@components/icon';
import Link from '@components/link';
import ContactForm from '@components/form';
import PropertyCard from '@components/card';
import Grid from '@layout/grid';
import Container from '@layout/container';
import Button from '@components/button';
import Accordion from '@components/accordion';
import Category from '@components/category/component';
import TopicCarousel from '@components/topic-carousel';
import Modal from '@components/modal';
import AddToContactsButton from '@components/add-to-contacts';

import {
  agent_header,
  agent_header_outer_container,
  agent_header_container,
  agent_header_img,
  agent_header_text,
  mobile_only,
  desktop_only,
  social_container,
  social_link,
  detail_container,
  form,
  content,
  search_buttons,
  new_listings_header,
  register_content,
  testimonial_container,
  testimonial_item,
  designation_item,
  featured_listings_container,
  contact_form,
  quickbuy_modal_content,
  quickbuy_header,
  quickbuy_content_container,
  quickbuy_content,
  quickbuy_content_img,
  quickbuy_container,
  add_to_contacts_button
} from './styles.module.scss';

export async function getServerSideProps(context) {
  const notFoundPage = { notFound: true };
  const slug = context.params.id;
  const possibleAgentId = parseInt(slug, 10);
  const agentId = isNaN(possibleAgentId) ? 0 : possibleAgentId;

  if (agentId <= 0) return notFoundPage;

  const data = await graphql(
    gql`
      ${AGENT_BY_ID_QUERY}
    `,
    { agentId: agentId }
  );

  if (!data.agentSearch.items[0]) {
    return notFoundPage;
  }

  const agentData = data.agentSearch.items[0];
  const officeId = parseInt(agentData?.officeId, 10);
  let officeData = null;

  if (officeId > 0) {
    // The office lookup only enriches the page and its schema — a failure here
    // must not take down the agent page, so fall back to the static office data.
    let apiOffice = null;
    try {
      const apiOfficeData = await graphql(
        gql`
          query GetAgentOfficeDetails($officeId: Int!) {
            officeSearch(where: { officeId: { eq: $officeId } }) {
              items {
                address
                city
                state
                zipCode
                name
                officeId
                phone
                fax
                photoUrl
                latitude
                longitude
              }
            }
          }
        `,
        { officeId }
      );
      apiOffice = apiOfficeData?.officeSearch?.items[0];
    } catch {
      // Already reported to Rollbar inside graphql(); static data covers the rest.
    }

    const staticOfficeData = officeList.find((office) => office.officeId === officeId);

    if (apiOffice || staticOfficeData) {
      const definedApiFields = Object.fromEntries(
        Object.entries(apiOffice || {}).filter(
          ([, value]) => value !== null && value !== undefined && value !== ''
        )
      );

      officeData = {
        ...staticOfficeData,
        ...definedApiFields,
        areasServing: staticOfficeData?.areasServing || [],
        statesServing: staticOfficeData?.statesServing || []
      };
    }
  }

  return {
    props: {
      data: agentData,
      officeData
    }
  };
}

export const getSocialLinks = (data) => {
  let socialLinks = [];
  if (data?.displayPhone) {
    socialLinks.push({ url: `tel:${data?.displayPhone}`, icon: 'phone' });
    socialLinks.push({ url: `sms:${data?.displayPhone}`, icon: 'text' });
  }
  if (data?.displayEmail) {
    socialLinks.push({ url: `mailto:${data?.displayEmail}`, icon: 'email' });
  }
  if (data?.displayWebsiteUrl) {
    socialLinks.push({ url: data.displayWebsiteUrl, icon: 'website' });
  }

  if (data?.socialMediaLinks) {
    socialLinks = socialLinks.concat(
      data.socialMediaLinks.map((link) => {
        return {
          url: link.linkUrl?.toLowerCase(),
          icon: link.description?.toLowerCase(),
          iconType: 'social'
        };
      })
    );
  }

  return socialLinks;
};

const AgentDetailPage = ({ data, officeData }) => {
  const router = useRouter();

  const { userInfo } = useUserInfo();
  const [newListings, setNewListings] = useState([]);
  const [soldListings, setSoldListings] = useState([]);
  const [hasListings, setHasListings] = useState(true);
  const [officeUrl, setOfficeUrl] = useState('');
  const [quickbuyModalOpen, setQuickbuyModalOpen] = useState(false);

  const defaultQuickBuyContent = (
    <>
      <h2>Request a Cash Offer</h2>
      <p>
        {data?.nickname || 'We'} can evaluate your property, discuss your options for selling your
        home, and answer any questions you may have about QuickBuy!
      </p>
    </>
  );

  const [quickBuyContent, setQuickBuyContent] = useState(defaultQuickBuyContent);

  useEffect(() => {
    if (!data?.agentId) {
      return;
    }

    if (router.query.quickbuy) {
      setQuickbuyModalOpen(true);
    }

    const fetchNewListings = async () => {
      const newListingData = await graphql(
        gql`
          ${LISTINGS_BY_AGENT_ID_QUERY}
        `,
        {
          agentId: parseInt(data.agentId),
          take: 9,
          skip: 0,
          sortBy: fixtures.sortButtonOptions[0].value,
          statusId: [1, 5]
        }
      );

      // If there are no listings, set hasListings to false and set the officeUrl
      if (newListingData.listingSearch.items.length === 0) {
        setHasListings(false);

        const officeData = await graphql(
          gql`
            query GetOfficeIdByAgentId($officeId: Int!) {
              officeSearch(where: { officeId: { eq: $officeId } }) {
                items {
                  name
                }
              }
            }
          `,
          {
            officeId: parseInt(data?.officeId)
          }
        );

        const officeName = officeData?.officeSearch?.items[0]?.name;

        if (officeName) {
          setOfficeUrl(`/office/${toKebabCase(officeName)}/listings`);
        }
      }

      if (newListingData.listingSearch.items.length < 6 || !newListingData.listingSearch.items) {
        const teamMemberIdArray = data.teamMembers.map((teamMember) => teamMember.id);
        teamMemberIdArray.push(parseInt(data.agentId));

        const teamListingData = await graphql(
          gql`
            ${LISTINGS_BY_AGENT_ID_PLUS_TEAM_QUERY}
          `,
          {
            teamMembers: teamMemberIdArray,
            take: 9,
            skip: 0,
            sortBy: fixtures.sortButtonOptions[0].value,
            statusId: [1, 5]
          }
        );

        const officeListings = await graphql(
          gql`
            ${NEW_OFFICE_LISTINGS}
          `,
          {
            officeId: data.officeId,
            take: 9
          }
        );

        const newListingsArray = [
          ...teamListingData.listingSearch.items,
          ...officeListings.listingSearch.items
        ].slice(0, 9);

        setNewListings(newListingsArray);
      } else {
        setNewListings([...newListingData.listingSearch.items].slice(0, 9));
      }
    };

    const fetchSoldListings = async () => {
      const soldListingData = await graphql(
        gql`
          ${LISTINGS_BY_AGENT_ID_QUERY}
        `,
        {
          agentId: parseInt(data.agentId),
          take: 6,
          skip: 0,
          sortBy: fixtures.sortButtonOptions[0].value,
          statusId: [6]
        }
      );

      setSoldListings(soldListingData.listingSearch.items || []);
    };

    fetchNewListings();
    fetchSoldListings();
  }, [data]);

  const openQuickbuyModal = () => {
    setQuickBuyContent(
      <>
        <h2>Schedule a Consultation</h2>
        <p>
          At a home seller consultation, we'll explore your options, discuss your goals and
          timelines, and talk selling strategy in today's market.
        </p>
      </>
    );
    setQuickbuyModalOpen(true);
  };

  const closeQuickbuyModal = () => {
    setQuickbuyModalOpen(false);
    setQuickBuyContent(defaultQuickBuyContent);
    // remove the quickbuy query param from the url
    router.replace(router.pathname, router.asPath.split('?')[0], { shallow: true });
  };

  const agentOfficeUrl = officeData?.name ? `/office/${toKebabCase(officeData.name)}` : null;

  const getLicensedIn = () => {
    let licensedIn = [];
    if (data?.isOhioLicensed) {
      licensedIn.push('Ohio');
    }
    if (data?.isKentuckyLicensed) {
      licensedIn.push('Kentucky');
    }
    if (data?.isIndianaLicensed) {
      licensedIn.push('Indiana');
    }

    return licensedIn.join(', ');
  };

  const getDesignations = () => {
    let agentDesignations;

    if (data?.designations) {
      // separate the agent designations by comma
      // Then map through the designations.json file to get the full designation object
      // Then push the full designation object to the agentDesignations array
      agentDesignations = data.designations
        .split(',')
        .map((designation) =>
          designations.find(
            (d) => d.acronym === designation.trim() || d.name === designation.trim()
          )
        )
        .filter((designation) => designation);
    }

    if (agentDesignations.length > 0) {
      // display designations with name and desciption below in html
      agentDesignations = agentDesignations.map((designation, index) => {
        return (
          <div key={'designation-' + index} className={designation_item}>
            <h3>{designation.name}</h3>
            <p>{designation.description}</p>
          </div>
        );
      });

      return agentDesignations;
    }
  };

  // --------------------------------------------------------

  const LIMIT = 3;

  const [testimonialsShown, setTestimonialsShown] = useState(LIMIT);
  const testimonials = data?.testimonials?.slice(0, testimonialsShown);
  const loadMore = () => {
    setTestimonialsShown(testimonialsShown + LIMIT);
  };

  // --------------------------------------------------------

  const getAccordionItems = () => {
    let items = [];
    if (data?.designations) {
      items.push({ label: 'Professional Designations', body: getDesignations() });
    }

    if (data?.testimonials.length > 0) {
      items.push({
        label: 'Testimonials',
        id: 'testimonials',
        body: (
          <div className={testimonial_container}>
            {testimonials?.map((testimonial, index) => (
              <div className={testimonial_item} key={index}>
                <p>{testimonial.testimonialText}</p>
                {testimonial.signature && <span>- {testimonial.signature}</span>}
              </div>
            ))}
            {data?.testimonials.length > testimonialsShown && (
              <Button label="Load More" onClick={loadMore} />
            )}
          </div>
        )
      });
    }

    return items;
  };

  const formatBio = (bio) => {
    return bio
      .split(/\.\s{2,}/g) // Splits text into chunks where there are multiple spaces or new sentences.
      .map((paragraph, index) => {
        const trimmed = paragraph.trim();
        const endsWithPunctuation = /[.!?]$/.test(trimmed);
        return <p key={index}>{endsWithPunctuation ? trimmed : `${trimmed}.`}</p>;
      });
  };

  return (
    <Layout
      meta={{
        title: data?.displayPrimary,
        description: `Learn more about buying or selling a home with ${data?.displayPrimary} and Sibcy Cline Realtors.`
      }}
      specializedPageSchema="agentSchema"
      agentDetails={{
        agent: data,
        office: officeData
          ? {
              ...officeData,
              url: agentOfficeUrl
            }
          : null
      }}
    >
      <div className={agent_header}>
        <div className={agent_header_outer_container}>
          <div className={agent_header_container}>
            <div className={classNames(agent_header_img, mobile_only)}>
              <Image
                image={{ height: '480', src: data?.displayPhotoUrl, width: '350' }}
                objectFit={'cover'}
                priority
                sizes="(max-width: 768px) 200px, 400px"
              />
            </div>

            <div className={agent_header_text}>
              <h1>{data?.displayPrimary}</h1>
              <h2>{data?.displaySecondary || data?.professionalTitle}</h2>
              <p>
                {data?.nickname} is licensed in {getLicensedIn()}.
              </p>
              {officeData?.name && (
                <p>
                  Office:{' '}
                  {agentOfficeUrl ? (
                    <Link url={agentOfficeUrl}>{officeData.name}</Link>
                  ) : (
                    officeData.name
                  )}
                  {officeData.address && (
                    <>
                      <br />
                      {officeData.address}, {officeData.city}, {officeData.state}{' '}
                      {officeData.zipCode}
                    </>
                  )}
                </p>
              )}
              {getSocialLinks(data).length > 0 && (
                <div className={social_container}>
                  {getSocialLinks(data).map((link) => (
                    <Link
                      key={link.url}
                      className={social_link}
                      url={link.url}
                      ariaLabel={link.icon}
                      target="_blank"
                    >
                      <Icon type={link?.iconType || 'actions'} name={link.icon} />
                    </Link>
                  ))}
                </div>
              )}
              <div className={classNames(search_buttons, desktop_only)}>
                <Button
                  label="Start Search"
                  url="/results"
                  query={{ status: ['Active', 'Pending'], agentid: data?.agentId }}
                />
                <Button
                  url="/register"
                  query={{ agentid: data?.agentId }}
                  label={`Register with ${data?.nickname || data?.displayPrimary}`}
                  theme="red-outline"
                />
                <Button
                  label="Get your home value"
                  theme="red-text-only-with-caret"
                  url={`https://homevalue.sibcycline.com/${data?.agentId}`}
                  query={{ agentid: data?.agentId }}
                />
              </div>
              <div className={classNames(search_buttons, mobile_only)}>
                <AddToContactsButton
                  className={add_to_contacts_button}
                  name={data?.displayPrimary}
                  phone={data?.displayPhone}
                  email={data?.displayEmail}
                  theme="text-only-red"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={classNames(agent_header_img, desktop_only)}>
        <Image
          image={{ height: '480', src: data?.displayPhotoUrl, width: '350' }}
          backgroundImage
          objectFit={'contain'}
          layout={'fill'}
          priority
          sizes="(max-width: 768px) 200px, 400px"
        />
      </div>
      <Container>
        <div className={detail_container}>
          <div className={classNames(form, desktop_only)}>
            <ContactForm
              className={contact_form}
              header={`Connect with ${data?.nickname}`}
              formDescription={
                <p>
                  <a href={`tel:${data.displayPhone}`}>{data.displayPhone}</a>
                </p>
              }
              submitLabel="Send Message"
              requestType="Agent"
              userInfo={userInfo}
              agentIds={data?.agentId ? [data?.agentId] : []}
            ></ContactForm>
          </div>

          <div className={content}>
            <div className={classNames(search_buttons, mobile_only)}>
              <Button
                label="Start Search"
                url="/results"
                query={{ status: ['Active', 'Pending'], agentid: data?.agentId }}
              />
              <Button
                url="/register"
                query={{ agentid: data?.agentId }}
                label={`Register with ${data?.nickname || data?.displayPrimary}`}
                theme="red-outline"
              />
              <Button
                label="Get your home value"
                theme="red-text-only-with-caret"
                url={`https://homevalue.sibcycline.com/${data?.agentId}`}
                query={{ agentid: data?.agentId }}
              />
            </div>

            {data?.displayBiography && formatBio(data?.displayBiography)}

            <div className={register_content}>
              <p>
                Save searches and listings, add notes, and streamline tour and info requests by{' '}
                <Link url="/register" query={{ agentid: data?.agentId }}>
                  creating an account and registering with {data?.nickname}
                </Link>
                .
              </p>
            </div>

            <Accordion
              listItems={{
                items: getAccordionItems()
              }}
              openAccordionId={['testimonials']}
            />

            <div className={classNames(form, mobile_only)}>
              <ContactForm
                className={contact_form}
                header={`Connect with ${data?.nickname}`}
                formDescription={
                  <p>
                    <a href={`tel:${data.displayPhone}`}>{data.displayPhone}</a>
                  </p>
                }
                submitLabel="Send Message"
                requestType="Agent"
                userInfo={userInfo}
                agentIds={data?.agentId ? [data?.agentId] : []}
              ></ContactForm>
            </div>
          </div>
        </div>
      </Container>

      {/* New Listings */}

      <>
        {newListings?.length > 0 && (
          <Container
            className={featured_listings_container}
            textAlignment="center"
            gridOptions={{ layout: 'single' }}
          >
            <div className={new_listings_header}>
              <h2>Featured Properties</h2>
              <p>Check out the latest properties presented by {data?.nickname} and team!</p>

              <Link
                url={
                  hasListings && officeUrl === '' ? `/agent/${data.agentId}/listings` : officeUrl
                }
              >
                View All <Icon type="arrow" name={'small-right'} />
              </Link>
            </div>
            {newListings.length > 3 ? (
              <TopicCarousel slidesCollection={{ cards: newListings }} theme="listings" />
            ) : (
              <Grid layout="third">
                {newListings.map((listing) => {
                  return (
                    <PropertyCard
                      alertPills={listing.alertPills}
                      image={{
                        src: !listing.mainPhoto
                          ? 'https://place-hold.it/340x231/green/white&text=FPO&fontsize=16'
                          : `${listing.mainPhoto.midSizeImageUrl}`
                      }}
                      property={{
                        ...listing,
                        details: [
                          { br: listing.bedroomCount },
                          {
                            ba: listing.partBathroomCount
                              ? `${listing.fullBathroomCount}/${listing.partBathroomCount}`
                              : listing.fullBathroomCount
                          },
                          { ac: listing.acres }
                        ],
                        price: listing.priceFormatted,
                        realtor: {
                          isActiveSibcyAgent: !listing.isIdxListing,
                          primaryRealtorName: listing.primaryAgentDisplayOne,
                          secondaryRealtorName: listing.secondaryAgentDisplayOne
                        }
                      }}
                      url={getSlug(listing.canonicalUrl)}
                      key={`card-${listing.listingId}`}
                    />
                  );
                })}
              </Grid>
            )}
          </Container>
        )}
      </>

      <Container className={quickbuy_container} gridOptions={{ layout: 'single' }}>
        <div className={quickbuy_header}>
          <h2>Sell Smart. Sell Confidently. Sell with Sibcy Cline.</h2>
          <Button label="Schedule a Consultation" onClick={openQuickbuyModal} />
        </div>
        <div className={quickbuy_content_container}>
          <Image
            className={quickbuy_content_img}
            image={{
              src: 'https://images.ctfassets.net/wzzep3ntdgx4/1rhEnpf5To3Bm0I98RAqvO/0f172c04374c59c5017aa5a49db5ad69/AdobeStock_293023394.jpeg',
              width: 393,
              height: 271
            }}
          />

          <div className={quickbuy_content}>
            <span>
              <h3>List with a Local Expert</h3>
              <p>
                We're ready to successfully list, market, and get top dollar for your home. We
                invest in professional photography, graphic design, and unparalleled online exposure
                to bring buyers beyond the yard sign!
              </p>
            </span>
            <span>
              <h3>Consider a Cash Offer</h3>
              <p>
                Receive a Sibcy Cline QuickBuy® Cash Offer on a qualified home and close in as few
                as 14 days, or lock in your offer while we try to get a better one on the open
                market! We utilize our market expertise to help you get the best iBuyer offer
                available.{' '}
                <Button
                  theme="red-text-only-with-caret"
                  label="Request an offer"
                  onClick={openQuickbuyModal}
                />
              </p>
            </span>
            <span>
              <h3>Leverage our Trusted Vendors</h3>
              <p>
                Whether you need help prepping your home for sale or moving out of it when you
                close, our Home Services Concierge is here to streamline and simplify your home sale
                process.
              </p>
            </span>
          </div>
        </div>
      </Container>

      {/* Sold Listings */}
      <>
        {soldListings?.length > 0 && (
          <Container textAlignment="center" gridOptions={{ layout: 'single' }}>
            <div className={new_listings_header}>
              <h2>Successfully Sold Homes</h2>
            </div>
            {soldListings.length > 3 ? (
              <TopicCarousel slidesCollection={{ cards: soldListings }} theme="listings" />
            ) : (
              <Grid layout="third">
                {soldListings.map((listing) => {
                  return (
                    <PropertyCard
                      alertPills={listing.alertPills}
                      image={{
                        src: !listing.mainPhoto
                          ? 'https://place-hold.it/340x231/green/white&text=FPO&fontsize=16'
                          : `${listing.mainPhoto.midSizeImageUrl}`
                      }}
                      property={{
                        ...listing,
                        details: [
                          { br: listing.bedroomCount },
                          {
                            ba: listing.partBathroomCount
                              ? `${listing.fullBathroomCount}/${listing.partBathroomCount}`
                              : listing.fullBathroomCount
                          },
                          { ac: listing.acres }
                        ],
                        price: listing.priceFormatted,
                        realtor: {
                          isActiveSibcyAgent: !listing.isIdxListing,
                          primaryRealtorName: listing.primaryAgentDisplayOne,
                          secondaryRealtorName: listing.secondaryAgentDisplayOne
                        }
                      }}
                      url={getSlug(listing.canonicalUrl)}
                      key={`card-${listing.listingId}`}
                    />
                  );
                })}
              </Grid>
            )}
          </Container>
        )}
      </>

      <Category
        title="Related Articles"
        slug="working-with-an-agent"
        categoryIds={['Working With an Agent']}
      />

      {quickbuyModalOpen && (
        <Modal
          modalIsOpen={quickbuyModalOpen}
          onModalClose={() => closeQuickbuyModal()}
          theme="pre-approval"
          content={
            <div>
              <div className={quickbuy_modal_content}>{quickBuyContent}</div>
              <ContactForm
                agentIds={data?.agentId ? [data?.agentId] : []}
                submitLabel="Submit"
                requestType="QuickBuy"
                shouldShowConsentToContact={true}
                optionalFields={{
                  items: [
                    {
                      type: 'Short Text',
                      name: 'Property Address or City/Suburb',
                      label: 'Property Address or City/Suburb',
                      required: true
                    }
                  ]
                }}
                shouldShowComments={false}
                setModalIsOpen={() => closeQuickbuyModal()}
              ></ContactForm>
            </div>
          }
        />
      )}
    </Layout>
  );
};

export default AgentDetailPage;
